import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Eye,
  EyeOff,
  Fingerprint,
  KeyRound,
  Landmark,
  Loader2,
  Shield,
} from 'lucide-react';
import {
  PORTAL_POST_CHECKOUT_HINT_KEY,
  getPortalPocketBaseUrl,
  getPortalRememberMePreference,
  persistPortalPbSession,
  formatPortalLoginError,
  isPbAuthRefreshNonRenewable,
  BRAND_PORTAL,
} from '../constants';
import {
  buildCustomerRecord,
  PORTAL_TEMP_LOGIN_PW_KEY,
  persistPortalCustomer,
  type PortalRole,
} from './portal/portalCustomerAccess';
import { passkeyAuthenticate } from './portal/webauthnClient';
import {
  fetchIdAustriaStatus,
  idAustriaLoginStartUrl,
  messageForIdaError,
} from './portal/idAustriaClient';
import { AuroraAuthLayout } from './arkorbit-ui/AuroraAuthLayout';
import { AURORA_AUTH_VIDEO_SRC } from './arkorbit-ui/aurora-media';
import './arkorbit-ui/aurora-auth.css';

interface CustomerLoginProps {
  onLogin: (token: string, customer: CustomerRecord) => void;
}

export interface CustomerRecord {
  id: string;
  email: string;
  name: string;
  kanzlei: string;
  /** Stabile Org-ID (NC/Docs). Leer bei Legacy ohne Feld. */
  kanzlei_id?: string;
  tier: 'standard' | 'premium';
  matrixRoomId: string;
  /** MXID dieses Sitzes. Room-Id ist kein Konto. */
  matrixUserId?: string;
  /** Element am eigenen Sitz. Ohne Schalter kein Reiter Kanzlei-Chat. */
  elementSeat?: boolean;
  status?: string;
  must_change_password?: boolean;
  portal_role?: PortalRole;
  is_primary?: boolean;
  account_kind?: 'beta_tester' | 'demo_prospect' | 'customer';
  /** Org-Lizenz: inkludierte Sitze (Vertrag). */
  included_seats?: number;
  /** Org-Lizenz: harter Deckel inkl. Nachkauf-Kontingent. */
  max_seats?: number;
  /** Sitz-Kategorien. Fehlendes Feld schaltet keinen Baustein frei. */
  baustein_kategorien?: string | Record<string, string>;
  /** Bestand, solange der Schlüssel JUDICATURE fehlt. */
  module_judicature?: boolean;
}

const CustomerLogin: React.FC<CustomerLoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => getPortalRememberMePreference());
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(() => {
    if (typeof window === 'undefined') return '';
    const code = new URLSearchParams(window.location.search).get('ida_error');
    return messageForIdaError(code);
  });
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [idAustriaConfigured, setIdAustriaConfigured] = useState(false);
  const [idAustriaStatusKnown, setIdAustriaStatusKnown] = useState(false);
  const [idAustriaLoading, setIdAustriaLoading] = useState(false);
  const [showPostPaymentHint, setShowPostPaymentHint] = useState(
    () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PORTAL_POST_CHECKOUT_HINT_KEY) === '1',
  );

  const supportsPasskeys =
    typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

  useEffect(() => {
    document.documentElement.setAttribute('data-portal-root', '');
    return () => {
      document.documentElement.removeAttribute('data-portal-root');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchIdAustriaStatus()
      .then((st) => {
        if (cancelled) return;
        setIdAustriaConfigured(st.configured);
        setIdAustriaStatusKnown(true);
      })
      .catch(() => {
        if (cancelled) return;
        setIdAustriaConfigured(false);
        setIdAustriaStatusKnown(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('ida_error')) {
      try {
        window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
      } catch {
        /* ignore */
      }
    }
  }, []);

  const handleIdAustriaLogin = () => {
    if (!idAustriaConfigured) return;
    setError('');
    setIdAustriaLoading(true);
    window.location.assign(idAustriaLoginStartUrl());
  };

  const persistLoginSession = (token: string, customer: CustomerRecord, pbBase: string) => {
    persistPortalPbSession(token, pbBase, rememberMe);
    persistPortalCustomer(customer);
  };

  const handlePasskeyLogin = async () => {
    if (!email.trim()) {
      setError('Bitte zuerst Ihre E-Mail-Adresse eingeben.');
      return;
    }
    setError('');
    setPasskeyLoading(true);
    try {
      const data = (await passkeyAuthenticate(email.trim())) as {
        token?: string;
        record?: CustomerRecord & { matrixRoomId?: string; tier?: string };
      };
      if (!data.token || !data.record?.id) {
        throw new Error('Passkey-Anmeldung: Session-Antwort unvollständig.');
      }
      const customer = buildCustomerRecord(data.record as unknown as Record<string, unknown>);
      persistLoginSession(data.token, customer, getPortalPocketBaseUrl());
      sessionStorage.removeItem(PORTAL_POST_CHECKOUT_HINT_KEY);
      onLogin(data.token, customer);
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e.name === 'NotAllowedError') {
        setError('Biometrische Anmeldung abgebrochen.');
      } else {
        setError(e.message || 'Passkey-Anmeldung fehlgeschlagen.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const pbBase = getPortalPocketBaseUrl();
    try {
      const res = await fetch(`${pbBase}/api/collections/customers/auth-with-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identity: email.trim(), password }),
      });

      let data: { record?: CustomerRecord; token?: string; message?: string } = {};
      try {
        data = await res.json();
      } catch {
        if (!res.ok) {
          setError(formatPortalLoginError(res.status, null, pbBase));
          return;
        }
      }

      if (!res.ok) {
        setError(formatPortalLoginError(res.status, data, pbBase));
        return;
      }

      if (!data.record?.id || !data.token) {
        setError('Anmeldung fehlgeschlagen: Ungültige Server-Antwort.');
        return;
      }

      const customer = buildCustomerRecord(data.record as unknown as Record<string, unknown>);
      if (customer.must_change_password && password) {
        sessionStorage.setItem(PORTAL_TEMP_LOGIN_PW_KEY, password);
      } else {
        sessionStorage.removeItem(PORTAL_TEMP_LOGIN_PW_KEY);
      }

      persistLoginSession(data.token, customer, pbBase);
      sessionStorage.removeItem(PORTAL_POST_CHECKOUT_HINT_KEY);
      onLogin(data.token, customer);
    } catch (err: unknown) {
      const isNetwork =
        err instanceof TypeError ||
        (err instanceof Error && /failed to fetch|network|load failed/i.test(err.message));
      setError(
        isNetwork
          ? formatPortalLoginError(0, null, pbBase)
          : 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTokenLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = accessToken.trim();
    if (!token) {
      setError('Bitte einen gültigen Access-Token eingeben.');
      return;
    }

    setError('');
    setTokenLoading(true);
    const pbBase = getPortalPocketBaseUrl();
    try {
      const res = await fetch(`${pbBase}/api/collections/customers/auth-refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      let data: { record?: CustomerRecord; token?: string; message?: string } = {};
      try {
        data = await res.json();
      } catch {
        if (!res.ok) {
          setError('Access-Token ungültig oder abgelaufen.');
          return;
        }
      }

      if (isPbAuthRefreshNonRenewable(res.status, data)) {
        /* Passkey/Impersonate-Token: gültig, nicht erneuerbar */
      } else if (!res.ok) {
        setError(data.message?.trim() || 'Access-Token ungültig oder abgelaufen.');
        return;
      }

      if (!data.record?.id) {
        setError('Token-Session: Kundendatensatz fehlt in der Antwort.');
        return;
      }

      const sessionToken = data.token?.trim() || token;
      const customer = buildCustomerRecord(data.record as unknown as Record<string, unknown>);
      persistLoginSession(sessionToken, customer, pbBase);
      sessionStorage.removeItem(PORTAL_POST_CHECKOUT_HINT_KEY);
      onLogin(sessionToken, customer);
    } catch (err: unknown) {
      const isNetwork =
        err instanceof TypeError ||
        (err instanceof Error && /failed to fetch|network|load failed/i.test(err.message));
      setError(
        isNetwork
          ? formatPortalLoginError(0, null, pbBase)
          : 'Token-Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const isBusy = loading || passkeyLoading || tokenLoading || idAustriaLoading;

  return (
    <AuroraAuthLayout
      videoSrc={AURORA_AUTH_VIDEO_SRC}
      videoOverlay="subtle"
      videoPlaybackRate={0.65}
    >
      <div className="aurora-auth__panel-inner">
        <header className="aurora-auth__panel-brand aurora-auth__panel-brand--centered">
          <div className="aurora-auth__panel-logo">
            <img src="/arkorbit-wappen.png" alt="" width={48} height={48} />
          </div>
          <p className="aurora-auth__panel-kicker">{BRAND_PORTAL}</p>
          <h2 className="aurora-auth__panel-title">Willkommen zurück</h2>
          <p className="aurora-auth__panel-sub">
            Melden Sie sich mit Ihren Kanzlei-Zugangsdaten an — für Demo, Test und Produktion.
          </p>
        </header>

        {showPostPaymentHint && (
          <div className="aurora-auth__hint-banner">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-400 mt-0.5" aria-hidden />
              <div className="space-y-2">
                <p className="font-semibold text-white">Zahlung erfolgreich – vielen Dank</p>
                <p className="leading-relaxed opacity-90">
                  Sie erhalten in Kürze eine E-Mail mit Ihren Zugangsdaten. Bitte prüfen Sie auch den Spam-Ordner.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem(PORTAL_POST_CHECKOUT_HINT_KEY);
                    setShowPostPaymentHint(false);
                  }}
                  className="text-xs opacity-60 hover:opacity-100 underline"
                >
                  Hinweis ausblenden
                </button>
              </div>
            </div>
          </div>
        )}

        <form className="aurora-auth__form" data-testid="portal-login" onSubmit={handleSubmit}>
          <div className="aurora-input-group">
            <label htmlFor="portal-email">Geschäftliche E-Mail</label>
            <input
              id="portal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="kanzlei@example.at"
              autoComplete="username"
            />
          </div>

          <div className="aurora-input-group">
            <label htmlFor="portal-password">Passwort</label>
            <div className="aurora-input-wrap">
              <input
                id="portal-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="aurora-auth__remember">
            <label className="aurora-auth__remember-label" htmlFor="portal-remember-me">
              <input
                id="portal-remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Angemeldet bleiben</span>
            </label>
            <p className="aurora-auth__remember-hint">
              Ohne Haken: Login nur in diesem Fenster (mehrere Accounts parallel möglich).
            </p>
          </div>

          {error && (
            <div className="aurora-auth__error" role="alert">
              <AlertCircle size={16} className="flex-shrink-0" />
              {error}
            </div>
          )}

          <button type="submit" className="aurora-btn-primary" disabled={isBusy}>
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Anmelden…
              </>
            ) : (
              'Sicher anmelden'
            )}
          </button>
        </form>

        {supportsPasskeys && (
          <>
            <div className="aurora-divider">oder</div>
            <button
              type="button"
              className="aurora-btn-secondary"
              onClick={handlePasskeyLogin}
              disabled={isBusy}
            >
              {passkeyLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Biometrie läuft…
                </>
              ) : (
                <>
                  <Fingerprint size={16} /> Mit Fingerabdruck / Face ID
                </>
              )}
            </button>
          </>
        )}

        <div className="aurora-auth__ida">
          <div className="aurora-divider">oder</div>
          <button
            type="button"
            className="aurora-btn-secondary"
            onClick={handleIdAustriaLogin}
            disabled={isBusy || !idAustriaConfigured}
            aria-disabled={!idAustriaConfigured}
          >
            {idAustriaLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Weiterleitung…
              </>
            ) : (
              <>
                <Landmark size={16} /> Mit ID Austria
              </>
            )}
          </button>
          {idAustriaStatusKnown && !idAustriaConfigured && (
            <p className="aurora-auth__ida-hint">
              Noch nicht auf diesem Server eingerichtet. Es wird keine Anmeldung vorgetäuscht —
              zuerst Passwort setzen, ID Austria später als Zusatz.
            </p>
          )}
          {idAustriaStatusKnown && idAustriaConfigured && (
            <p className="aurora-auth__ida-hint">
              Wenn ID Austria nicht erreichbar ist, gelten Passwort und Passkey unverändert.
            </p>
          )}
        </div>

        <details className="aurora-auth__internal">
          <summary className="aurora-auth__internal-summary">
            <KeyRound size={14} aria-hidden />
            Interner Zugang
            <ChevronDown size={14} className="aurora-auth__internal-chevron" aria-hidden />
          </summary>
          <form className="aurora-auth__internal-form" onSubmit={handleTokenLogin}>
            <p className="aurora-auth__internal-hint">
              Nur für interne Tests: PocketBase-Access-Token direkt einfügen.
            </p>
            <div className="aurora-input-group">
              <label htmlFor="portal-access-token">Access-Token</label>
              <input
                id="portal-access-token"
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="eyJhbGciOiJIUzI1NiIs…"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <button type="submit" className="aurora-btn-secondary" disabled={isBusy}>
              {tokenLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Token wird geprüft…
                </>
              ) : (
                'Mit Token anmelden'
              )}
            </button>
          </form>
        </details>

        <p className="aurora-auth__trust">
          <Shield size={12} className="text-emerald-400" />
          Ende-zu-Ende verschlüsselt · Server Frankfurt · 100% DSGVO
        </p>

        <p className="aurora-auth__footer-link">
          Zugangsprobleme?{' '}
          <a href="mailto:office@arkorbit.at">office@arkorbit.at</a>
        </p>
      </div>
    </AuroraAuthLayout>
  );
};

export default CustomerLogin;
